import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Sparkles, 
  Search, 
  ChevronDown, 
  ChevronRight,
  MessageCircle,
  Mail,
  Book,
  Video,
  FileText,
  Users,
  Zap,
  Shield,
  CreditCard,
  Settings,
  HelpCircle,
  ExternalLink,
  Phone,
  Clock
} from 'lucide-react';
import ContactForm from '../components/support/ContactForm';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const HelpPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const helpCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      description: 'Learn the basics of Quixocial',
      icon: Sparkles,
      color: 'from-blue-500 to-cyan-500',
      articles: [
        {
          title: 'Creating your first post',
          content: 'Learn how to create engaging social media posts using our AI-powered content generation. Navigate to the Posts section, click "Create New Post", and follow the step-by-step wizard to generate compelling content for your audience.'
        },
        {
          title: 'Setting up your profile',
          content: 'Complete your profile setup by adding your personal information, profile picture, and preferences. Go to Account Settings from the profile dropdown to customize your experience and set up your brand voice.'
        },
        {
          title: 'Understanding the dashboard',
          content: 'The dashboard provides an overview of your social media performance, recent activity, and quick actions. Explore the different widgets to track your content performance, manage clients, and access key features.'
        },
        {
          title: 'Connecting social media accounts',
          content: 'Connect your Facebook, Instagram, LinkedIn, and other social media accounts to start publishing content. Visit the Social Connections page to securely link your accounts and manage publishing permissions.'
        }
      ]
    },
    {
      id: 'content-creation',
      title: 'Content Creation',
      description: 'Master AI-powered content generation',
      icon: FileText,
      color: 'from-purple-500 to-pink-500',
      articles: [
        {
          title: 'Using AI content generation',
          content: 'Our AI uses Google Gemini to create engaging content. Provide a topic, target audience, and tone, and our AI will generate multiple post variations. You can refine and customize the content before publishing.'
        },
        {
          title: 'Creating engaging posts',
          content: 'Learn best practices for creating posts that drive engagement. Use compelling headlines, include relevant hashtags, add high-quality images, and maintain a consistent brand voice across all your content.'
        },
        {
          title: 'Managing your brand kit',
          content: 'Upload your brand assets including logos, colors, fonts, and style guidelines. The brand kit ensures consistent branding across all your generated content and helps the AI understand your brand voice.'
        },
        {
          title: 'Content templates and styles',
          content: 'Save time by creating reusable content templates. Set up different styles for various post types, campaigns, or clients. Templates help maintain consistency and speed up your content creation process.'
        }
      ]
    },
    {
      id: 'scheduling',
      title: 'Scheduling & Publishing',
      description: 'Plan and schedule your content',
      icon: Clock,
      color: 'from-green-500 to-emerald-500',
      articles: [
        {
          title: 'Scheduling posts',
          content: 'Schedule your posts for optimal engagement times. Use the content calendar to plan your posting schedule, set specific dates and times, and let the platform automatically publish your content across all connected social media accounts.'
        },
        {
          title: 'Best times to post',
          content: 'Our analytics suggest optimal posting times based on your audience engagement patterns. Generally, weekdays between 9 AM - 3 PM perform well, but check your specific audience insights for personalized recommendations.'
        },
        {
          title: 'Bulk scheduling',
          content: 'Save time by scheduling multiple posts at once. Upload a CSV file with your content, or use our bulk scheduler to plan weeks of content in advance. Perfect for campaigns and consistent posting schedules.'
        },
        {
          title: 'Managing your content calendar',
          content: 'Use the visual content calendar to see your posting schedule at a glance. Drag and drop posts to reschedule, view upcoming content, and ensure consistent posting across all your social media channels.'
        }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics & Insights',
      description: 'Track your performance',
      icon: Zap,
      color: 'from-orange-500 to-red-500',
      articles: [
        {
          title: 'Understanding analytics',
          content: 'Track key metrics like engagement rate, reach, impressions, and follower growth. Our analytics dashboard provides insights into which content performs best and when your audience is most active.'
        },
        {
          title: 'Performance metrics',
          content: 'Monitor important KPIs including likes, comments, shares, click-through rates, and conversion metrics. Use these insights to optimize your content strategy and improve engagement over time.'
        },
        {
          title: 'Audience insights',
          content: 'Learn about your audience demographics, interests, and behavior patterns. Understanding your audience helps you create more targeted content that resonates with your followers and drives better results.'
        },
        {
          title: 'Growth tracking',
          content: 'Monitor your follower growth, engagement trends, and content performance over time. Set goals and track your progress to measure the success of your social media strategy and campaigns.'
        }
      ]
    },
    {
      id: 'team-collaboration',
      title: 'Team & Clients',
      description: 'Collaborate with your team',
      icon: Users,
      color: 'from-indigo-500 to-purple-500',
      articles: [
        {
          title: 'Managing clients',
          content: 'Create separate client profiles to organize content and campaigns. Each client can have their own brand kit, content calendar, and team members. Switch between clients easily from the client selector.'
        },
        {
          title: 'Team collaboration',
          content: 'Invite team members to collaborate on content creation and management. Assign different roles and permissions to control what each team member can access and modify within the platform.'
        },
        {
          title: 'User permissions',
          content: 'Set up role-based access control for your team. Admins have full access, editors can create and modify content, and viewers can only see content and analytics. Customize permissions as needed.'
        },
        {
          title: 'Client reporting',
          content: 'Generate comprehensive reports for your clients showing performance metrics, content published, and growth achieved. Schedule automatic report delivery or create custom reports for client presentations.'
        }
      ]
    },
    {
      id: 'account-billing',
      title: 'Account & Billing',
      description: 'Manage your account and subscription',
      icon: CreditCard,
      color: 'from-pink-500 to-rose-500',
      articles: [
        {
          title: 'Account settings',
          content: 'Manage your personal information, password, notification preferences, and security settings. Keep your account secure by enabling two-factor authentication and regularly updating your password.'
        },
        {
          title: 'Billing and payments',
          content: 'View your billing history, update payment methods, and manage your subscription. All payments are processed securely, and you can download invoices for your records at any time.'
        },
        {
          title: 'Upgrading your plan',
          content: 'Upgrade to Pro or Enterprise plans to access advanced features like unlimited AI generations, team collaboration, and priority support. Compare plans on our pricing page to find the best fit for your needs.'
        },
        {
          title: 'Managing credits',
          content: 'Monitor your AI generation credits and usage. Credits reset monthly based on your plan. Pro and Enterprise users get more credits and can purchase additional credits if needed for high-volume content creation.'
        }
      ]
    }
  ];

  const faqs = [
    {
      id: 'what-is-social-sphere',
      question: 'What is Quixocial?',
      answer: 'Quixocial is an AI-powered content management platform that helps businesses and creators generate, schedule, and analyze their content across multiple platforms.'
    },
    {
      id: 'how-ai-works',
      question: 'How does the AI content generation work?',
      answer: 'Our AI uses Google Gemini to generate high-quality, engaging content based on your brand voice, target audience, and content preferences. Simply provide a topic or brief, and our AI will create compelling posts tailored to your needs.'
    },
    {
      id: 'supported-platforms',
      question: 'Which social media platforms do you support?',
      answer: 'We currently support Facebook, Instagram, LinkedIn, Twitter/X, and YouTube. We\'re continuously adding support for more platforms based on user demand.'
    },
    {
      id: 'pricing-plans',
      question: 'What are the different pricing plans?',
      answer: 'We offer three plans: Free (basic features with limited credits), Pro (advanced features with more credits), and Enterprise (unlimited access with premium support). Visit our pricing page for detailed information.'
    },
    {
      id: 'data-security',
      question: 'How secure is my data?',
      answer: 'We take data security seriously. All data is encrypted in transit and at rest, we use enterprise-grade AWS infrastructure, and we comply with GDPR, CCPA, and other privacy regulations.'
    },
    {
      id: 'team-collaboration',
      question: 'Can I collaborate with my team?',
      answer: 'Yes! You can invite team members, assign different roles and permissions, manage multiple clients, and collaborate on content creation and scheduling.'
    },
    {
      id: 'content-ownership',
      question: 'Who owns the content created with AI?',
      answer: 'You own all content created using our platform. The AI-generated content is provided as a starting point, and you have full rights to use, modify, and publish it as you see fit.'
    },
    {
      id: 'cancel-subscription',
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes, you can cancel your subscription at any time. Your account will remain active until the end of your current billing period, and you can continue using the service until then.'
    }
  ];

  const [showContactForm, setShowContactForm] = useState(false);

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">


      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-black dark:text-white mb-4">
            How can we help you?
          </h1>
          <p className="text-xl text-black dark:text-gray-300 mb-8">
            Find answers, get support, and learn how to make the most of Quixocial
          </p>
          
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500 dark:text-gray-400" />
            <Input
              type="text"
              placeholder="Search for help articles, FAQs, or topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-4 text-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-2xl shadow-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Help Categories */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-black dark:text-white mb-8 text-center">
            Browse by Category
          </h2>
          {!selectedCategory ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {helpCategories.map((category) => (
                <Card 
                  key={category.id} 
                  className="group hover:shadow-lg transition-all duration-200 cursor-pointer border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                  onClick={() => setSelectedCategory(category.id)}
                >
                  <CardHeader className="pb-3">
                    <div className={`w-12 h-12 bg-gradient-to-br ${category.color} rounded-lg flex items-center justify-center mb-3`}>
                      <category.icon className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-lg text-black dark:text-white">{category.title}</CardTitle>
                    <CardDescription className="text-black dark:text-gray-300">
                      {category.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {category.articles.map((article, index) => (
                        <li key={index} className="flex items-center text-sm text-slate-800 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          <ChevronRight className="w-4 h-4 mr-2 flex-shrink-0" />
                          {article.title}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="max-w-4xl mx-auto">
              <div className="mb-6">
                <Button 
                  variant="ghost" 
                  onClick={() => setSelectedCategory(null)}
                  className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Categories
                </Button>
              </div>
              
              {(() => {
                const category = helpCategories.find(cat => cat.id === selectedCategory);
                if (!category) return null;
                
                return (
                  <div>
                    <div className="flex items-center mb-8">
                      <div className={`w-16 h-16 bg-gradient-to-br ${category.color} rounded-lg flex items-center justify-center mr-4`}>
                        <category.icon className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h2 className="text-3xl font-bold text-black dark:text-white">{category.title}</h2>
                        <p className="text-slate-800 dark:text-gray-300 mt-1">{category.description}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-6">
                      {category.articles.map((article, index) => (
                        <Card key={index} className="border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                          <CardHeader>
                            <CardTitle className="text-xl text-black dark:text-white flex items-center">
                              <Book className="w-5 h-5 mr-3 text-blue-500" />
                              {article.title}
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-slate-800 dark:text-gray-300 leading-relaxed">
                              {article.content}
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-black dark:text-white mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="max-w-4xl mx-auto">
            {filteredFaqs.map((faq) => (
              <div key={faq.id} className="mb-4">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                  className="w-full text-left p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-black dark:text-white pr-4">
                      {faq.question}
                    </h3>
                    <ChevronDown className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                      expandedFaq === faq.id ? 'transform rotate-180' : ''
                    }`} />
                  </div>
                  {expandedFaq === faq.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-slate-800 dark:text-gray-300 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Support */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-black dark:text-white mb-8 text-center">
            Still Need Help?
          </h2>
          <div className="max-w-2xl mx-auto">
            {!showContactForm ? (
              <Card className="text-center border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <CardHeader>
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl text-black dark:text-white">Email Support</CardTitle>
                  <CardDescription className="text-black dark:text-gray-300">
                    Send us a detailed message and we'll get back to you
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                    Response within 24 hours
                  </p>
                  <p className="text-sm font-medium text-black dark:text-gray-300 mb-4">
                    info@quixocial.com
                  </p>
                  <Button 
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white"
                    onClick={() => setShowContactForm(true)}
                  >
                    Contact Support
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div>
                <div className="mb-6">
                  <Button 
                    variant="ghost" 
                    onClick={() => setShowContactForm(false)}
                    className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Support Options
                  </Button>
                </div>
                <ContactForm 
                  onSuccess={() => {
                    // Keep the form visible to show success message
                  }}
                  onError={(error) => {
                    console.error('Contact form error:', error);
                  }}
                />
              </div>
            )}
          </div>
        </section>


      </div>
    </div>
  );
};

export default HelpPage;